// SPDX-License-Identifier: Elastic-2.0
/**
 * ConnectionBanner — surface the WS reconnect / reconnected lifecycle (T-023).
 *
 * Listens to the `useWebSocket()` Zustand store (T-008) and renders:
 *
 *   1. **Reconnecting** (warning) — visible only after `disconnectedSinceMs >= 5000`
 *      AND `status !== "open"`. The 5s grace prevents banner flicker on
 *      a single missed ping. Copy interpolates the elapsed time, updating
 *      every ~1s as the store's `disconnectedSinceMs` ticks.
 *
 *   2. **Reconnected** (success) — flashes for 2s after `status` flips
 *      back to `"open"` from a previously-shown disconnect, then auto-hides.
 *      We mount this transient banner only when a disconnect was visible;
 *      a clean boot (open → no disconnect ever) shows nothing.
 *
 * Renders nothing in steady-state (connected, no recent disconnect) — the
 * component returns `null` so the DOM stays quiet.
 *
 * Why the success banner is local state rather than store-driven:
 *   - The store knows "connected now" but doesn't track "we were showing
 *     the banner". Putting the success-flash flag here keeps the store
 *     pure (it's the WS truth, not a UI presentation log).
 *   - Auto-dismiss is a presentation concern; a 2s `setTimeout` belongs
 *     in the consumer that knows the design's chosen flash duration.
 *
 * Per F-310 functional spec § 12 (WS disconnect UX) and task T-023.
 */
import type { ReactElement } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Banner } from "@/components/Banner/Banner";
import { useWebSocket } from "@/api/useWebSocket";

import styles from "./ConnectionBanner.module.css";

/** Match the 5s threshold from functional spec § 12. */
const SHOW_THRESHOLD_MS = 5_000;
/** Brief success banner duration. Long enough to read, short enough not to nag. */
const RECONNECT_FLASH_MS = 2_000;

/**
 * `disconnectedSinceMs` ticks every 1s — convert to a whole-second count
 * for the i18n interpolation. We floor to avoid showing "5 s" twice (once
 * at 5.0s and again at 5.4s flooring to the same value).
 */
function elapsedSeconds(ms: number): number {
  return Math.floor(ms / 1000);
}

export function ConnectionBanner(): ReactElement | null {
  const { t } = useTranslation();
  const status = useWebSocket((s) => s.status);
  const disconnectedSinceMs = useWebSocket((s) => s.disconnectedSinceMs);

  // Local "we just reconnected — show the success flash" flag. Driven by
  // a useEffect that watches status transitions; auto-cleared after 2s.
  const [showReconnected, setShowReconnected] = useState(false);

  // Track whether the warning banner was previously visible, so the
  // success flash only fires after a disconnect the user actually saw.
  // A boot-time open without ever crossing the threshold should stay quiet.
  const sawDisconnectRef = useRef(false);

  const reconnecting =
    status !== "open" &&
    disconnectedSinceMs !== null &&
    disconnectedSinceMs >= SHOW_THRESHOLD_MS;

  // Remember when the warning surfaces so the next "open" can flash success.
  useEffect(() => {
    if (reconnecting) {
      sawDisconnectRef.current = true;
    }
  }, [reconnecting]);

  // On reconnect after a visible disconnect, flash success for 2s.
  useEffect(() => {
    if (status !== "open") return;
    if (!sawDisconnectRef.current) return;
    sawDisconnectRef.current = false;
    setShowReconnected(true);
    const handle = window.setTimeout(() => setShowReconnected(false), RECONNECT_FLASH_MS);
    return () => window.clearTimeout(handle);
  }, [status]);

  if (reconnecting) {
    const seconds = elapsedSeconds(disconnectedSinceMs);
    // First tick of the visible banner is at the 5s threshold; before then
    // we already returned null. Showing the "(N s)" copy as soon as we're
    // visible is intentional — users get an immediate signal that we're
    // counting elapsed time, not just stuck on "Reconnecting…".
    const message =
      seconds > 0
        ? t("dashboard.connection.reconnecting_with_seconds", { seconds })
        : t("dashboard.connection.reconnecting");
    return (
      <div className={styles.wrap} data-testid="connection-banner">
        <Banner kind="warning" message={message} />
      </div>
    );
  }

  if (showReconnected) {
    return (
      <div className={styles.wrap} data-testid="connection-banner-reconnected">
        <Banner kind="success" message={t("dashboard.connection.reconnected")} />
      </div>
    );
  }

  return null;
}

export default ConnectionBanner;
